import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useHistoryStore } from '@/lib/store/history-store';
import { useDeepSearchListener } from '@/lib/hooks/useDeepSearchListener';
import { SocialLinks } from '@/components/search/social-links';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { DeepSearchData } from '@/lib/types';
import { ExternalLink, Briefcase, GraduationCap } from 'lucide-react';
import { MoreInfoSheet } from '@/components/search/more-info-sheet';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { logAnalyticsEvent } from '@/lib/firebase/analytics';

export default function SearchPersonResultPage() {
  const { getSearch, userId } = useHistoryStore();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const [showConfirmation, setShowConfirmation] = useState(true);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const hasLoggedComplete = useRef(false);

  // Listen for real-time updates from Firestore
  useDeepSearchListener(userId, id || '');

  useEffect(() => {
    document.title = 'Person Search Results - Deepcheck';
  }, []);

  const search = id ? getSearch(id) : null;
  const result = search?.data as DeepSearchData | undefined;

  // Log search_complete when results are available
  useEffect(() => {
    if (result && search?.status === 'completed' && !hasLoggedComplete.current) {
      logAnalyticsEvent('search_complete', { type: 'person' });
      hasLoggedComplete.current = true;
    }
  }, [result, search?.status]);

  // Show loading while waiting for data
  if (!search || search.status === 'analyzing' || search.status === 'queued') {
    return (
      <main className="px-4 py-6 flex flex-col items-center justify-center min-h-[60vh]">
        <LoadingSpinner />
        <p className="mt-4 text-gray-600">Researching...</p>
        {search && <p className="text-sm text-gray-400 mt-2">Progress: {search.progress}%</p>}
      </main>
    );
  }

  // Show error state
  if (search.status === 'failed') {
    return (
      <main className="px-4 py-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h2 className="text-red-800 font-semibold">Search Failed</h2>
          <p className="text-red-600 text-sm mt-1">{search.error?.message || 'An error occurred'}</p>
        </div>
      </main>
    );
  }

  if (!result) {
    return (
      <main className="px-4 py-6">
        <LoadingSpinner />
      </main>
    );
  }

  return (
    <main className="px-4 py-6 space-y-6">
      {/* Header */}
      <h1 className="text-xl font-bold text-gray-900">{result.name || result.title}</h1>

      {/* Aliases */}
      {result.aliases && result.aliases.length > 0 && (
        <div className="flex flex-col gap-2">
          {result.aliases.map((alias, index) => (
            <Badge key={index} variant="outline" className="text-sm whitespace-normal text-left py-2 px-3 h-auto">
              {alias}
            </Badge>
          ))}
        </div>
      )}

      {/* Social Profiles */}
      {result.socialProfiles && result.socialProfiles.length > 0 && (
        <SocialLinks profiles={result.socialProfiles} />
      )}

      {/* Professional Info */}
      {result.professionalInfo && (
        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Briefcase className="w-4 h-4" />
            Professional Info
          </h2>
          {result.professionalInfo.currentRole && (
            <p className="text-sm text-gray-700">
              <span className="font-medium">{result.professionalInfo.currentRole}</span>
              {result.professionalInfo.company && ` at ${result.professionalInfo.company}`}
            </p>
          )}
          {result.professionalInfo.industry && (
            <p className="text-xs text-gray-500">Industry: {result.professionalInfo.industry}</p>
          )}
          
          {/* Experience */}
          {result.professionalInfo.experience && result.professionalInfo.experience.length > 0 && (
            <div className="mt-3 space-y-2">
              <h3 className="text-sm font-medium text-gray-700">Experience</h3>
              {result.professionalInfo.experience.slice(0, 3).map((exp, i) => (
                <div key={i} className="text-sm bg-white p-2 rounded">
                  <p className="font-medium">{exp.title}</p>
                  <p className="text-gray-600">{exp.company}</p>
                  {exp.dates && <p className="text-xs text-gray-400">{exp.dates}</p>}
                </div>
              ))}
            </div>
          )}

          {/* Education */}
          {result.professionalInfo.education && result.professionalInfo.education.length > 0 && (
            <div className="mt-3 space-y-2">
              <h3 className="text-sm font-medium text-gray-700 flex items-center gap-1">
                <GraduationCap className="w-4 h-4" />
                Education
              </h3>
              {result.professionalInfo.education.map((edu, i) => (
                <div key={i} className="text-sm bg-white p-2 rounded">
                  <p className="font-medium">{edu.institution}</p>
                  {edu.degree && <p className="text-gray-600">{edu.degree}{edu.field && ` in ${edu.field}`}</p>}
                  {edu.years && <p className="text-xs text-gray-400">{edu.years}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Key Facts */}
      {result.keyFacts && result.keyFacts.length > 0 && (
        <div className="space-y-2">
          <h2 className="font-semibold text-gray-900">Key Facts</h2>
          <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
            {result.keyFacts.map((fact, index) => (
              <li key={index}>{fact}</li>
            ))}
          </ul>
        </div>
      )}

      {/* News Articles */}
      {result.newsArticles && result.newsArticles.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-semibold text-gray-900">News & Articles</h2>
          {result.newsArticles.slice(0, 5).map((article, index) => (
            <a
              key={index}
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-white border rounded-lg p-3 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900 text-sm">{article.title}</h3>
                  <p className="text-xs text-gray-500 mt-1">{article.source}{article.date && ` • ${article.date}`}</p>
                  <p className="text-sm text-gray-600 mt-2 line-clamp-2">{article.snippet}</p>
                </div>
                <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
              </div>
            </a>
          ))}
        </div>
      )}

      {/* Sources Badge */}
      {result.sources && result.sources.length > 0 && (
        <div>
          <Badge variant="secondary" className="text-sm cursor-pointer" onClick={() => setIsSheetOpen(true)}>
            {result.sources.length} sources
          </Badge>
        </div>
      )}

      {/* Summary/Description */}
      <div className="space-y-2 pb-32">
        <h2 className="font-semibold text-gray-900">Summary</h2>
        <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{result.summary}</p>
      </div>

      {/* Confirmation Floating Bar */}
      {showConfirmation && (
        <div className="fixed bottom-8 left-0 right-0 z-40 flex justify-center px-4">
          <div className="w-full max-w-[396px] bg-[#E5E5E5] p-5 rounded-[32px] shadow-lg space-y-4">
            <p className="text-center text-gray-900 font-medium text-base">
              Is this the person you were looking for?
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => setShowConfirmation(false)}
                className="flex-1 bg-white hover:bg-gray-50 text-black font-medium rounded-full h-12 shadow-sm border-0"
              >
                Yes
              </Button>
              <Button
                onClick={() => setIsSheetOpen(true)}
                className="flex-1 bg-white hover:bg-gray-50 text-black font-medium rounded-full h-12 shadow-sm border-0"
              >
                No
              </Button>
            </div>
          </div>
        </div>
      )}

      <MoreInfoSheet open={isSheetOpen} onOpenChange={setIsSheetOpen} sources={result.sources} />
    </main>
  );
}
